/**
 * useMarket — single source of truth for "which market am I rendering for?".
 *
 * Resolution order:
 *   1. Explicit override passed as the hook arg (e.g. property's country)
 *   2. URL prefix on the current pathname (/uk/... → GB, /us/... → US)
 *   3. useGeoBucket() detection (timezone + sticky localStorage)
 *   4. Default → IE
 *
 * The URL step means Footer / Navbar / any shared chrome auto-adapts to the
 * market without every render tree having to thread a `countryOverride`
 * prop down through five levels of component nesting.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useGeoBucket } from './useGeoBucket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// In-process cache keyed by canonical country code. The /info response is
// effectively static for a given market so one fetch per session is fine.
const _CACHE = new Map();

// Synchronous fallback used while the backend response is still in flight.
// Matches the IE adapter so first paint never shows blank labels.
const FALLBACK_IE = {
  code: 'IE',
  name: 'Ireland',
  currency: 'EUR',
  currency_symbol: '€',
  locale: 'en-IE',
  og_locale: 'en_IE',
  area_unit_label: 'County',
  sub_area_unit_label: 'Area',
  postal_unit_label: 'Eircode',
  postal_example: 'D04 X2P5',
  popular_areas: [],
  regulator_label: 'PSRA',
  regulator_url: '',
  url_prefix: '',
  search_placeholder: 'e.g. 3 bed house under €300k in Wicklow, or 40 min commute of Citywest',
  area_example_pair: ['Ballinteer, Dublin', 'Malahide, Dublin'],
};

const FALLBACK_UK = {
  ...FALLBACK_IE,
  code: 'GB', name: 'United Kingdom', currency: 'GBP', currency_symbol: '£',
  locale: 'en-GB', og_locale: 'en_GB',
  area_unit_label: 'Postcode area', sub_area_unit_label: 'Outward',
  postal_unit_label: 'Postcode', postal_example: 'SW1A 1AA',
  regulator_label: 'Propertymark', url_prefix: '/uk',
  search_placeholder: 'e.g. 3 bed flat under £500k in Clapham, or 40 min commute of Canary Wharf',
  area_example_pair: ['Clapham, London', 'Didsbury, Manchester'],
};

const FALLBACK_US = {
  ...FALLBACK_IE,
  code: 'US', name: 'United States', currency: 'USD', currency_symbol: '$',
  locale: 'en-US', og_locale: 'en_US',
  area_unit_label: 'State', sub_area_unit_label: 'Neighborhood',
  postal_unit_label: 'ZIP code', postal_example: '10001',
  regulator_label: 'NAR', url_prefix: '/us',
  search_placeholder: 'e.g. 3 bed home under $500k in Austin, or 40 min commute of Downtown',
  area_example_pair: ['Williamsburg, Brooklyn', 'Mission District, San Francisco'],
};

const FALLBACKS = { IE: FALLBACK_IE, GB: FALLBACK_UK, UK: FALLBACK_UK, US: FALLBACK_US, USA: FALLBACK_US };

// Normalise any free-text country string to one of our canonical codes.
// Mirrors the backend MarketAdapter aliases.
const canonicalCode = (s) => {
  if (!s) return null;
  const x = String(s).trim().toUpperCase();
  if (['UK', 'GB', 'UNITED KINGDOM', 'GREAT BRITAIN', 'ENGLAND', 'SCOTLAND', 'WALES', 'NORTHERN IRELAND'].includes(x)) return 'GB';
  if (['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(x)) return 'US';
  if (['IE', 'IRL', 'IRELAND', 'ROI', 'EI'].includes(x)) return 'IE';
  return null;
};

// Inspect the current pathname for a market prefix. Runs safely in SSR /
// tests where `window` is undefined.
//
// IMPORTANT: any pathname that is NOT explicitly under /uk or /us is
// treated as IE — including '/'. This makes the root URL deterministic
// (an IE landing) instead of letting the geo-bucket override the market
// on first paint and inject '/us' or '/uk' into every internal link.
// The market switcher in the navbar remains the ONLY way to change
// the market from a root '/' visit.
const codeFromPath = () => {
  if (typeof window === 'undefined') return null;
  const path = window.location?.pathname || '';
  if (path === '/uk' || path.startsWith('/uk/')) return 'GB';
  if (path === '/us' || path.startsWith('/us/')) return 'US';
  return 'IE';
};

export const useMarket = (override) => {
  const { bucket } = useGeoBucket();
  const code = canonicalCode(override) || codeFromPath() || canonicalCode(bucket) || 'IE';
  const [profile, setProfile] = useState(() => _CACHE.get(code) || FALLBACKS[code] || FALLBACK_IE);

  useEffect(() => {
    let cancelled = false;
    // Sync to the fallback profile for this code immediately so a code change
    // (SPA navigation between /faq and /uk/faq, or a change of geo-bucket)
    // flips the currency/labels without waiting for the API round-trip.
    if (_CACHE.has(code)) {
      setProfile(_CACHE.get(code));
    } else if (FALLBACKS[code]) {
      setProfile(FALLBACKS[code]);
    }
    axios.get(`${BACKEND_URL}/api/markets/info`, { params: { country: code } })
      .then((r) => {
        if (cancelled || !r?.data) return;
        // Merge with the client-side FALLBACK so fields the backend doesn't
        // yet return (search_placeholder, area_example_pair, etc.) survive
        // the API response and downstream components stay market-aware.
        const merged = { ...(FALLBACKS[code] || FALLBACK_IE), ...r.data };
        _CACHE.set(code, merged);
        setProfile(merged);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [code]);

  return useMemo(() => {
    const isUK = profile.code === 'GB';
    const isIE = profile.code === 'IE';
    const isUS = profile.code === 'US';
    const formatCurrency = (n) => {
      if (n == null || isNaN(n)) return '—';
      return new Intl.NumberFormat(profile.locale, {
        style: 'currency', currency: profile.currency, maximumFractionDigits: 0,
      }).format(Number(n));
    };
    // Prefix a relative path with the market's URL prefix.
    //   ie  withPrefix('/sold/D06') → '/sold/D06'
    //   uk  withPrefix('/sold/W5')  → '/uk/sold/W5'
    //   us  withPrefix('/sold/100') → '/us/sold/100'
    const withPrefix = (path) => `${profile.url_prefix || ''}${path}`;
    return { ...profile, isUK, isIE, isUS, formatCurrency, withPrefix };
  }, [profile]);
};

export default useMarket;
